import React, { useState, useEffect, useCallback } from 'react';
import { 
  Users, 
  Shield, 
  AlertTriangle, 
  Clock, 
  TrendingUp,
  TrendingDown,
  Star,
  Calendar,
  XCircle,
  CheckCircle,
  Activity,
  RefreshCw,
  AlertCircle,
  Award,
  Zap,
  UserX,
  CalendarOff,
  ChevronRight,
  Smartphone
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import api from '@/lib/api';

const WorkforceIntelligencePage = () => {
  const [loading, setLoading] = useState(true);
  const [performanceDashboard, setPerformanceDashboard] = useState(null);
  const [blockedStaff, setBlockedStaff] = useState(null);
  const [leaveConflicts, setLeaveConflicts] = useState(null);
  const [fatigueAlerts, setFatigueAlerts] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [perfRes, blockedRes, conflictsRes, fatigueRes] = await Promise.all([
        api.get('/workforce-intelligence/performance/dashboard'),
        api.get('/workforce-intelligence/compliance/blocked-staff'),
        api.get('/workforce-intelligence/leave/all-conflicts?days_ahead=14'),
        api.get('/workforce-intelligence/fatigue/alerts')
      ]);
      
      setPerformanceDashboard(perfRes.data?.data);
      setBlockedStaff(blockedRes.data?.data);
      setLeaveConflicts(conflictsRes.data?.data);
      setFatigueAlerts(fatigueRes.data?.data);
    } catch (err) {
      toast.error(err.message || 'Failed to load workforce data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getStatusBadge = (status) => {
    const config = {
      'EXCELLENT': { color: 'bg-green-500', icon: Star },
      'GOOD': { color: 'bg-blue-500', icon: CheckCircle },
      'NEEDS_IMPROVEMENT': { color: 'bg-yellow-500', icon: AlertCircle },
      'CONCERN': { color: 'bg-red-500', icon: AlertTriangle }
    };
    const { color, icon: Icon } = config[status] || { color: 'bg-gray-500', icon: Users };
    return (
      <Badge className={`${color} text-white flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {status.replace('_', ' ')}
      </Badge>
    );
  };

  const getScoreColor = (score) => {
    if (score >= 85) return 'text-green-600';
    if (score >= 70) return 'text-blue-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const SummaryCard = ({ title, value, subtitle, icon: Icon, color, alert }) => (
    <Card className={`border-l-4 ${color}`} data-testid={`summary-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500">{title}</p>
            <p className="text-3xl font-bold mt-1">{value}</p>
            {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-full ${alert ? 'bg-red-100' : 'bg-gray-100'}`}>
            <Icon className={`w-6 h-6 ${alert ? 'text-red-600' : 'text-gray-600'}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="workforce-intelligence-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="w-7 h-7 text-blue-600" />
            Workforce Intelligence
          </h1>
          <p className="text-gray-600 mt-1">
            Performance scoring, compliance blocking, fatigue alerts
          </p>
        </div>
        <Button variant="outline" onClick={fetchData} data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard
          title="Total Staff"
          value={performanceDashboard?.summary?.total_staff || 0}
          subtitle={`Avg score: ${performanceDashboard?.summary?.average_score || 0}`}
          icon={Users}
          color="border-blue-500"
        />
        <SummaryCard
          title="Blocked Staff"
          value={blockedStaff?.blocked_count || 0}
          subtitle="Cannot be rostered"
          icon={UserX}
          color="border-red-500"
          alert={(blockedStaff?.blocked_count || 0) > 0}
        />
        <SummaryCard
          title="Leave Conflicts"
          value={leaveConflicts?.total_conflicts || 0}
          subtitle="Next 14 days"
          icon={CalendarOff}
          color="border-yellow-500"
          alert={(leaveConflicts?.total_conflicts || 0) > 0}
        />
        <SummaryCard
          title="Fatigue Alerts"
          value={fatigueAlerts?.total_alerts || 0}
          subtitle={`${fatigueAlerts?.critical_count || 0} critical`}
          icon={Activity}
          color="border-orange-500"
          alert={(fatigueAlerts?.critical_count || 0) > 0}
        />
      </div>

      {/* Status Breakdown */}
      {performanceDashboard && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Staff Performance Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-green-50 rounded-lg text-center" data-testid="status-excellent">
                <Award className="w-8 h-8 mx-auto text-green-600 mb-2" />
                <p className="text-3xl font-bold text-green-700">
                  {performanceDashboard.summary.status_breakdown.EXCELLENT || 0}
                </p>
                <p className="text-sm text-green-600">Excellent</p>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg text-center" data-testid="status-good">
                <CheckCircle className="w-8 h-8 mx-auto text-blue-600 mb-2" />
                <p className="text-3xl font-bold text-blue-700">
                  {performanceDashboard.summary.status_breakdown.GOOD || 0}
                </p>
                <p className="text-sm text-blue-600">Good</p>
              </div>
              <div className="p-4 bg-yellow-50 rounded-lg text-center" data-testid="status-needs-improvement">
                <AlertCircle className="w-8 h-8 mx-auto text-yellow-600 mb-2" />
                <p className="text-3xl font-bold text-yellow-700">
                  {performanceDashboard.summary.status_breakdown.NEEDS_IMPROVEMENT || 0}
                </p>
                <p className="text-sm text-yellow-600">Needs Improvement</p>
              </div>
              <div className="p-4 bg-red-50 rounded-lg text-center" data-testid="status-concern">
                <AlertTriangle className="w-8 h-8 mx-auto text-red-600 mb-2" />
                <p className="text-3xl font-bold text-red-700">
                  {performanceDashboard.summary.status_breakdown.CONCERN || 0}
                </p>
                <p className="text-sm text-red-600">Concern</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs defaultValue="performance" className="space-y-4">
        <TabsList>
          <TabsTrigger value="performance" data-testid="tab-performance">
            <TrendingUp className="w-4 h-4 mr-2" />
            Performance
          </TabsTrigger>
          <TabsTrigger value="compliance" data-testid="tab-compliance">
            <Shield className="w-4 h-4 mr-2" />
            Compliance
          </TabsTrigger>
          <TabsTrigger value="leave" data-testid="tab-leave">
            <Calendar className="w-4 h-4 mr-2" />
            Leave Conflicts
          </TabsTrigger>
          <TabsTrigger value="fatigue" data-testid="tab-fatigue">
            <Activity className="w-4 h-4 mr-2" />
            Fatigue
          </TabsTrigger>
        </TabsList>

        {/* Performance Tab */}
        <TabsContent value="performance">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Top Performers */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Star className="w-5 h-5 text-yellow-500" />
                  Top Performers
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {performanceDashboard?.top_performers?.length === 0 ? (
                    <p className="text-center text-gray-500 py-4">No performance data yet</p>
                  ) : (
                    performanceDashboard?.top_performers?.map((staff, i) => (
                      <div 
                        key={staff.staff_id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                        data-testid={`top-performer-${i}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                            i === 0 ? 'bg-yellow-500' : i === 1 ? 'bg-gray-400' : i === 2 ? 'bg-orange-400' : 'bg-blue-400'
                          }`}>
                            {i + 1}
                          </div>
                          <div>
                            <p className="font-medium">{staff.staff_name}</p>
                            <p className="text-xs text-gray-500">
                              {staff.shifts_completed_30d} shifts completed
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`text-xl font-bold ${getScoreColor(staff.overall_score)}`}>
                            {staff.overall_score}
                          </p>
                          <p className="text-xs text-gray-500">score</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Needs Attention */}
            <Card className="border-yellow-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg text-yellow-800">
                  <AlertCircle className="w-5 h-5" />
                  Needs Attention
                </CardTitle>
                <CardDescription>
                  {performanceDashboard?.needs_attention?.length || 0} staff members
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {performanceDashboard?.needs_attention?.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-2" />
                      <p>All staff performing well!</p>
                    </div>
                  ) : (
                    performanceDashboard?.needs_attention?.map((staff, i) => (
                      <div 
                        key={staff.staff_id}
                        className="p-3 border rounded-lg"
                        data-testid={`needs-attention-${i}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <p className="font-medium">{staff.staff_name}</p>
                          {getStatusBadge(staff.status)}
                        </div>
                        <div className="flex items-center justify-between text-sm text-gray-500">
                          <span>Score: {staff.overall_score}</span>
                          <span>Rating: {staff.participant_rating}/5</span>
                        </div>
                        {staff.alerts?.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {staff.alerts.map((alert, ai) => (
                              <p key={ai} className="text-xs text-red-600 flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3" />
                                {alert}
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Compliance Tab */}
        <TabsContent value="compliance">
          <Card className={blockedStaff?.blocked_count > 0 ? 'border-red-200' : ''}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Shield className="w-5 h-5 text-red-500" />
                Blocked from Rostering
              </CardTitle>
              <CardDescription>
                Staff who cannot be assigned to shifts due to compliance issues
              </CardDescription>
            </CardHeader>
            <CardContent>
              {blockedStaff?.blocked_count === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-2" />
                  <p>All staff are compliant</p>
                  <p className="text-sm">No rostering blocks active</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {blockedStaff?.blocked_staff?.map((staff, i) => (
                    <div 
                      key={staff.staff_id}
                      className="p-4 bg-red-50 border border-red-200 rounded-lg"
                      data-testid={`blocked-staff-${i}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-medium text-red-800">{staff.staff_name}</p>
                        <Badge variant="destructive">BLOCKED</Badge>
                      </div>
                      <p className="text-sm text-red-700 mb-2">{staff.block_reason}</p>
                      
                      {staff.expired_documents?.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs font-medium text-red-600 mb-1">Expired Documents:</p>
                          <div className="flex flex-wrap gap-1">
                            {staff.expired_documents.map((doc, di) => (
                              <Badge key={di} variant="outline" className="text-xs border-red-300 text-red-700">
                                {doc.document_type} (exp: {doc.expiry_date})
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {staff.missing_requirements?.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs font-medium text-red-600 mb-1">Missing Documents:</p>
                          <div className="flex flex-wrap gap-1">
                            {staff.missing_requirements.map((req, ri) => (
                              <Badge key={ri} variant="outline" className="text-xs border-red-300 text-red-700">
                                {req}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Leave Conflicts Tab */}
        <TabsContent value="leave">
          <Card className={leaveConflicts?.total_conflicts > 0 ? 'border-yellow-200' : ''}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Calendar className="w-5 h-5 text-yellow-500" />
                Leave Conflicts
              </CardTitle>
              <CardDescription>
                Conflicts detected for the next 14 days
              </CardDescription>
            </CardHeader>
            <CardContent>
              {leaveConflicts?.total_conflicts === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-2" />
                  <p>No leave conflicts detected</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {leaveConflicts?.conflicts?.map((conflict, i) => (
                    <div 
                      key={i}
                      className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg"
                      data-testid={`leave-conflict-${i}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-medium">{conflict.staff_name}</p>
                        <Badge className={
                          conflict.conflict_type === 'APPROVED_LEAVE' ? 'bg-red-500' :
                          conflict.conflict_type === 'PENDING_LEAVE' ? 'bg-yellow-500' :
                          'bg-gray-500'
                        }>
                          {conflict.conflict_type.replace('_', ' ')}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600 mb-1">
                        {conflict.leave_type}: {conflict.leave_start} to {conflict.leave_end}
                      </p>
                      {conflict.shift_id && (
                        <p className="text-xs text-red-600">
                          Shift assigned during leave period
                        </p>
                      )}
                      <p className="text-xs text-gray-500 mt-2 italic">
                        {conflict.resolution_suggestion}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Fatigue Tab */}
        <TabsContent value="fatigue">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Critical Alerts */}
            <Card className="border-red-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg text-red-800">
                  <AlertTriangle className="w-5 h-5" />
                  Critical Fatigue
                </CardTitle>
                <CardDescription>
                  {fatigueAlerts?.critical_count || 0} staff at critical fatigue level
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {fatigueAlerts?.alerts?.filter(a => a.alert_level === 'CRITICAL').length === 0 ? (
                    <div className="text-center py-4 text-gray-500">
                      <CheckCircle className="w-8 h-8 mx-auto text-green-500 mb-2" />
                      <p className="text-sm">No critical alerts</p>
                    </div>
                  ) : (
                    fatigueAlerts?.alerts?.filter(a => a.alert_level === 'CRITICAL').map((alert, i) => (
                      <div 
                        key={i}
                        className="p-3 bg-red-50 border border-red-200 rounded-lg"
                        data-testid={`fatigue-critical-${i}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <p className="font-medium text-red-800">{alert.staff_name}</p>
                          <Badge variant="destructive">CRITICAL</Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <p className="text-gray-600">Today: {alert.hours_worked_today}h</p>
                          <p className="text-gray-600">Week: {alert.hours_worked_week}h</p>
                          <p className="text-gray-600">Consecutive: {alert.consecutive_days}d</p>
                          <p className="text-gray-600">Rest: {alert.rest_hours_since_last}h</p>
                        </div>
                        <p className="text-xs text-red-600 mt-2">{alert.recommendations[0]}</p>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Warning Alerts */}
            <Card className="border-yellow-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg text-yellow-800">
                  <AlertCircle className="w-5 h-5" />
                  Fatigue Warnings
                </CardTitle>
                <CardDescription>
                  {fatigueAlerts?.warning_count || 0} staff with fatigue warnings
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {fatigueAlerts?.alerts?.filter(a => a.alert_level === 'WARNING').length === 0 ? (
                    <div className="text-center py-4 text-gray-500">
                      <CheckCircle className="w-8 h-8 mx-auto text-green-500 mb-2" />
                      <p className="text-sm">No warnings</p>
                    </div>
                  ) : (
                    fatigueAlerts?.alerts?.filter(a => a.alert_level === 'WARNING').map((alert, i) => (
                      <div 
                        key={i}
                        className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg"
                        data-testid={`fatigue-warning-${i}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <p className="font-medium">{alert.staff_name}</p>
                          <Badge className="bg-yellow-500">WARNING</Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <p className="text-gray-600">Today: {alert.hours_worked_today}h</p>
                          <p className="text-gray-600">Week: {alert.hours_worked_week}h</p>
                          <p className="text-gray-600">Consecutive: {alert.consecutive_days}d</p>
                          <p className="text-gray-600">Rest: {alert.rest_hours_since_last}h</p>
                        </div>
                        <p className="text-xs text-yellow-700 mt-2">{alert.recommendations[0]}</p>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Mobile API Note */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <Smartphone className="w-6 h-6 text-blue-600 mt-1" />
            <div>
              <p className="font-medium text-blue-800">Mobile-Ready APIs Available</p>
              <p className="text-sm text-blue-600">
                Quick shift actions (check-in/out, breaks), instant alerts, and offline-ready data structures are available via <code className="bg-blue-100 px-1 rounded">/api/workforce-intelligence/mobile/*</code>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default WorkforceIntelligencePage;
